import { useTranslation } from "react-i18next";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { formatByCurrency } from "../../../utils/formatCurrency";
import PriceRangeChart from "./PriceRangeChart";
import ActivityCompareChart from "./ActivityCompareChart";

const formatPriceRange = (min, max, currency) => {
  if (min == null && max == null) return "—";
  if (min != null && max != null && min !== max) {
    return `${formatByCurrency(min, currency)} - ${formatByCurrency(max, currency)}`;
  }
  return formatByCurrency(min ?? max, currency);
};


function TrendBadge({
  direction,
  pctChange,
  insufficientData,
  currentAvg,
  previousAvg,
  currentCount,
  currency,
  t,
}) {
  if (insufficientData) {
    return (
      <span className="dpi-trend-stack">
        <span className="dpi-muted">{t("admin.insights.competitorIntelligence.notEnoughData")}</span>
        {currentCount > 0 && currentAvg != null ? (
          <span className="dpi-trend-range">
            {t("admin.insights.competitorIntelligence.currentAvgOnly", {
              value: formatByCurrency(currentAvg, currency),
            })}
          </span>
        ) : null}
      </span>
    );
  }
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const tone = direction === "up" ? "up" : direction === "down" ? "down" : "flat";
  return (
    <span className="dpi-trend-stack">
      <span className={`dpi-trend-badge dpi-trend-badge--${tone}`}>
        <Icon size={13} />
        {pctChange != null ? `${pctChange > 0 ? "+" : ""}${pctChange}%` : t(`admin.insights.competitorIntelligence.direction.${tone}`)}
      </span>
      <span className="dpi-trend-range">
        {formatByCurrency(currentAvg, currency)}{" "}
        {t("admin.insights.competitorIntelligence.vsAvgLastMonth", {
          value: formatByCurrency(previousAvg, currency),
        })}
      </span>
    </span>
  );
}

function ActivityBadge({ current, previous, delta, direction, t }) {
  const Icon = direction === "up" ? TrendingUp : direction === "down" ? TrendingDown : Minus;
  const tone = direction === "up" ? "up" : direction === "down" ? "down" : "flat";
  return (
    <span className={`dpi-trend-badge dpi-trend-badge--${tone}`}>
      <Icon size={13} />
      {current}
      <span className="dpi-muted">
        {t("admin.insights.competitorIntelligence.vsPrevious", { count: previous })}
      </span>
      {delta !== 0 ? ` (${delta > 0 ? "+" : ""}${delta})` : null}
    </span>
  );
}

export default function CompetitorIntelligencePanel({
  data,
  loading = false,
  hasDateRange = false,
}) {
  const { t } = useTranslation();

  if (!hasDateRange) {
    return (
      <div className="dpi-chart-empty">
        {t("admin.insights.competitorIntelligence.selectDateRange")}
      </div>
    );
  }
  if (loading) {
    return <div className="dpi-chart-empty">{t("admin.insights.competitorIntelligence.loading")}</div>;
  }
  if (!data || !Array.isArray(data.competitors) || !data.competitors.length) {
    return <div className="dpi-chart-empty">{t("admin.insights.competitorIntelligence.empty")}</div>;
  }

  const { own, competitors } = data;
  const displayCurrency =
    own?.price_range_currency ||
    competitors.find((competitor) => competitor.price_range_currency)?.price_range_currency;

  return (
    <div className="dpi-op dpi-op--comp">
      <div className="dpi-ci-own">
        <span className="dpi-ci-own-label">{t("admin.insights.competitorIntelligence.ourPriceRange")}</span>
        <span className="dpi-ci-own-value">
          {formatPriceRange(own?.price_range_min, own?.price_range_max, own?.price_range_currency)}
        </span>
        <TrendBadge
          direction={own?.price_trend?.direction}
          pctChange={own?.price_trend?.pct_change}
          insufficientData={own?.price_trend?.insufficient_data}
          currentAvg={own?.price_trend?.current_avg}
          previousAvg={own?.price_trend?.previous_avg}
          currentCount={own?.price_trend?.current_count}
          currency={displayCurrency}
          t={t}
        />
      </div>

      <div className="dpi-ci-charts">
        <div className="dpi-ci-chart-block">
          <h4 className="dpi-ci-chart-title">
            {t("admin.insights.competitorIntelligence.priceChartTitle")}
          </h4>
          <p className="dpi-ci-chart-note">
            {t("admin.insights.competitorIntelligence.priceChartNote")}
          </p>
          <PriceRangeChart own={own} competitors={competitors} currency={displayCurrency} />
        </div>
        <div className="dpi-ci-chart-block">
          <h4 className="dpi-ci-chart-title">
            {t("admin.insights.competitorIntelligence.activityChartTitle")}
          </h4>
          <p className="dpi-ci-chart-note">
            {t("admin.insights.competitorIntelligence.activityChartNote")}
          </p>
          <ActivityCompareChart competitors={competitors} />
        </div>
      </div>

      <div className="dpi-op-scroll">
        <table className="dpi-op-table">
          <thead>
            <tr>
              <th className="dpi-op-th-outlet">{t("admin.insights.competitorIntelligence.competitor")}</th>
              <th>{t("admin.insights.competitorIntelligence.theirPriceRange")}</th>
              <th>{t("admin.insights.competitorIntelligence.priceTrend")}</th>
              <th>{t("admin.insights.competitorIntelligence.activityTrend")}</th>
              <th className="dpi-num">{t("admin.insights.competitorIntelligence.promotions")}</th>
            </tr>
          </thead>
          <tbody>
            {competitors.map((competitor) => (
              <tr key={competitor.competitor_id} className="dpi-op-row dpi-op-row--group">
                <td className="dpi-op-outlet">
                  <span className="dpi-op-outlet-name" title={competitor.competitor_name}>
                    {competitor.competitor_name}
                  </span>
                </td>
                <td className="dpi-muted">
                  {formatPriceRange(
                    competitor.price_range_min,
                    competitor.price_range_max,
                    competitor.price_range_currency,
                  )}
                </td>
                <td>
                  <TrendBadge
                    direction={competitor.price_trend?.direction}
                    pctChange={competitor.price_trend?.pct_change}
                    insufficientData={competitor.price_trend?.insufficient_data}
                    currentAvg={competitor.price_trend?.current_avg}
                    previousAvg={competitor.price_trend?.previous_avg}
                    currentCount={competitor.price_trend?.current_count}
                    currency={displayCurrency}
                    t={t}
                  />
                </td>
                <td>
                  <ActivityBadge
                    current={competitor.activity_trend?.current_proofs ?? 0}
                    previous={competitor.activity_trend?.previous_proofs ?? 0}
                    delta={competitor.activity_trend?.delta ?? 0}
                    direction={competitor.activity_trend?.direction}
                    t={t}
                  />
                </td>
                <td className="dpi-num">{competitor.promotion_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
